<template>
  <div data-test="image-uploader-btn" @click.prevent>
    <div v-if="Object.keys(imagesPreviews).length" class="previews">
      <uploaded-image-preview
        v-for="(machine, id, index) in imagesPreviews"
        :key="id"
        :machine="machine"
        :should-focus="shouldFocusImage(index)"
      />
    </div>
    <filestack class="button-wrapper" @imagesUploaded="saveLinks">
      <translate v-if="isEmpty"> Upload some photos </translate>
      <translate v-else> Upload another photo </translate>
    </filestack>
  </div>
</template>

<script setup lang="ts">
import { Actor } from 'xstate-v4/lib/Actor'
import { ImageUploaderStateMachineInterpreter } from '../machines/ImageUploaderMachine'
import { FilestackImagesWithDimensions } from '../types'
import { getImageUploaderInterpreterFactory } from '../inversify.config'
import type UploadedImagePreview from './UploadedImagePreview.vue'

const emit = defineEmits(['photosValid', 'input'])

const state = ref<ImageUploaderStateMachineInterpreter['state'] | null>(null)

const imagesPreviews = ref<Record<string, Actor>>({})
const previousImageCount = ref(0)
const userHasUploaded = ref(false)

const props = defineProps<{
  campsiteSlug: string
  token: string
}>()

const machine = getImageUploaderInterpreterFactory()(
  props.campsiteSlug,
  props.token,
  (useRoute().params.lang as string) || 'en-gb',
)
startMachine(machine)

const isEmpty = computed<boolean>(
  () => Object.keys(imagesPreviews.value).length === 0,
)

function shouldFocusImage(index: number): boolean {
  // Only focus if the user has just completed an upload
  if (!userHasUploaded.value) {
    return false
  }

  // If no photos have previously been uploaded, focus the first one
  if (previousImageCount.value === 0 && index === 0) {
    return true
  }

  // If we had photos already but the user uploads another one, focus on the first newly added one
  if (previousImageCount.value > 0 && index === previousImageCount.value) {
    return true
  }

  return false
}

function saveLinks(filestackImages: FilestackImagesWithDimensions) {
  userHasUploaded.value = true
  machine.send('NEW_IMAGES', { filestackImages })
}

function startMachine(machine: ImageUploaderStateMachineInterpreter) {
  machine
    .onChange((context) => {
      if (
        JSON.stringify(imagesPreviews.value) !==
        JSON.stringify(context.imagesPreviews)
      ) {
        previousImageCount.value = Object.keys(imagesPreviews.value).length
        imagesPreviews.value = context.imagesPreviews
        emit('input', Object.keys(context.imagesPreviews))

        // Reset the upload flag after the DOM has updated
        void nextTick(() => {
          userHasUploaded.value = false
        })
      }
    })
    .onTransition((s: ImageUploaderStateMachineInterpreter['state']) => {
      state.value = s
      if (s.matches('valid')) {
        emit('photosValid', true)
      } else {
        emit('photosValid', false)
      }
    })
    .start()
}
</script>
